import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useContent } from '@/context/ContentContext';
import { api, formatApiErrorDetail } from '@/lib/api';
import { LogOut, RefreshCw, Save, Trash2, Mail, MailOpen, Undo2 } from 'lucide-react';

const TABS = [
  { key: 'content', label: 'Content' },
  { key: 'messages', label: 'Inbox' },
];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const { content, setContent, saveContent, refresh } = useContent();
  const nav = useNavigate();
  const [tab, setTab] = useState('content');
  const [lang, setLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);

  useEffect(() => {
    if (checked && (!user || user.role !== 'admin')) nav('/admin/login', { replace: true });
  }, [checked, user, nav]);

  const loadMessages = async () => {
    setLoadingMsgs(true);
    try {
      const { data } = await api.get('/messages');
      setMessages(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => {
    if (tab === 'messages') loadMessages();
  }, [tab]);

  const onSave = async () => {
    if (!content) return;
    setSaving(true);
    try {
      await saveContent({ en: content.en, el: content.el });
      toast.success('Content saved.');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm('Reset all content to defaults?')) return;
    try {
      const { data } = await api.post('/content/reset');
      setContent(data);
      toast.success('Content reset to defaults.');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const setPath = (path, value) => {
    if (!content) return;
    const next = structuredClone(content);
    const parts = path.split('.');
    let cursor = next[lang];
    for (let i = 0; i < parts.length - 1; i++) {
      const key = /^\d+$/.test(parts[i]) ? Number(parts[i]) : parts[i];
      cursor = cursor[key];
    }
    const last = parts[parts.length - 1];
    cursor[/^\d+$/.test(last) ? Number(last) : last] = value;
    setContent(next);
  };

  const onDeleteMsg = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      if (selectedMsg?.id === id) setSelectedMsg(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onMarkRead = async (id) => {
    try {
      await api.patch(`/messages/${id}/read`);
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, read: true } : x)));
    } catch (_) {}
  };

  if (!checked || !user) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center text-ink-dim font-mono text-xs uppercase tracking-widest">
        …loading
      </div>
    );
  }

  const data = content?.[lang];

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[rgba(5,4,3,0.9)] backdrop-blur-xl border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="dot" />
              <span className="font-serif text-xl">Rotama<span className="text-ink-dim font-mono text-[10px] uppercase tracking-[0.25em] ml-2">Studio</span></span>
            </div>
            <nav className="flex items-center gap-2">
              {TABS.map((tb) => (
                <button
                  key={tb.key}
                  onClick={() => setTab(tb.key)}
                  data-testid={`admin-tab-${tb.key}`}
                  className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] rounded-full border ${
                    tab === tb.key
                      ? 'border-ember text-ember'
                      : 'border-transparent text-ink-dim hover:text-ink'
                  }`}
                >
                  {tb.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-dim hover:text-ink link-ember"
            >
              View site ↗
            </a>
            <button
              onClick={async () => { await logout(); nav('/admin/login'); }}
              data-testid="admin-logout"
              className="btn-ghost !px-3 !py-1.5 !text-[10px]"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === 'content' && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-3">
                  § CMS · Editable content
                </div>
                <h1 className="font-serif text-4xl md:text-5xl leading-tight">Landing copy</h1>
                <p className="text-ink-dim mt-2 max-w-lg text-sm">
                  Every visible field on the site is edited here. Language edits are per-locale.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] border border-[var(--line)] rounded-full px-1 py-1">
                  {['en', 'el'].map((L) => (
                    <button
                      key={L}
                      onClick={() => setLang(L)}
                      data-testid={`admin-lang-${L}`}
                      className={`px-2.5 py-1 rounded-full ${lang === L ? 'bg-ember text-paper' : 'text-ink-dim hover:text-ink'}`}
                    >
                      {L}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => refresh()}
                  className="btn-ghost !px-3 !py-1.5 !text-[10px]"
                  title="Reload from server"
                  data-testid="admin-refresh"
                >
                  <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Reload
                </button>
                <button
                  onClick={onReset}
                  className="btn-ghost !px-3 !py-1.5 !text-[10px]"
                  data-testid="admin-reset"
                >
                  <Undo2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Reset defaults
                </button>
                <button
                  onClick={onSave}
                  disabled={saving}
                  data-testid="admin-save"
                  className="btn-ember !px-4 !py-2 !text-[11px] disabled:opacity-60"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {saving ? 'Saving' : 'Save changes'}
                </button>
              </div>
            </div>

            {data && (
              <div className="space-y-14">
                <Group title="Hero">
                  <TextField label="Eyebrow" value={data.hero.eyebrow} onChange={(v) => setPath('hero.eyebrow', v)} />
                  <TextField label="Headline · line 1" value={data.hero.line1} onChange={(v) => setPath('hero.line1', v)} />
                  <TextField label="Headline · line 2" value={data.hero.line2} onChange={(v) => setPath('hero.line2', v)} />
                  <TextField label="Headline · line 3" value={data.hero.line3} onChange={(v) => setPath('hero.line3', v)} />
                  <TextArea label="Subline" value={data.hero.sub} onChange={(v) => setPath('hero.sub', v)} />
                  <div className="grid grid-cols-2 gap-6">
                    <TextField label="Primary CTA" value={data.hero.cta_primary} onChange={(v) => setPath('hero.cta_primary', v)} />
                    <TextField label="Secondary CTA" value={data.hero.cta_secondary} onChange={(v) => setPath('hero.cta_secondary', v)} />
                  </div>
                </Group>

                <Group title="Marquee">
                  <TextArea label="Marquee text" value={data.marquee} onChange={(v) => setPath('marquee', v)} />
                </Group>

                <Group title="Services">
                  <TextField label="Title" value={data.services.title} onChange={(v) => setPath('services.title', v)} />
                  <TextField label="Subtitle" value={data.services.subtitle} onChange={(v) => setPath('services.subtitle', v)} />
                  <div className="space-y-6">
                    {data.services.items.map((it, i) => (
                      <div key={i} className="border border-[var(--line)] p-6 bg-paper-2">
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-ember mb-4">Service {i + 1}</div>
                        <div className="grid grid-cols-2 gap-6 mb-4">
                          <TextField label="Code" value={it.code} onChange={(v) => setPath(`services.items.${i}.code`, v)} />
                          <TextField label="Title" value={it.title} onChange={(v) => setPath(`services.items.${i}.title`, v)} />
                        </div>
                        <TextArea label="Description" value={it.desc} onChange={(v) => setPath(`services.items.${i}.desc`, v)} />
                        <TextArea
                          label="Bullets (one per line)"
                          value={(it.bullets || []).join('\n')}
                          onChange={(v) => setPath(`services.items.${i}.bullets`, v.split('\n'))}
                        />
                      </div>
                    ))}
                  </div>
                </Group>

                <Group title="Process">
                  <TextField label="Title" value={data.process.title} onChange={(v) => setPath('process.title', v)} />
                  <TextField label="Subtitle" value={data.process.subtitle} onChange={(v) => setPath('process.subtitle', v)} />
                  <div className="space-y-6">
                    {data.process.chapters.map((c, i) => (
                      <div key={i} className="border border-[var(--line)] p-6 bg-paper-2">
                        <div className="grid grid-cols-2 gap-6 mb-4">
                          <TextField label="Number" value={c.num} onChange={(v) => setPath(`process.chapters.${i}.num`, v)} />
                          <TextField label="Title" value={c.title} onChange={(v) => setPath(`process.chapters.${i}.title`, v)} />
                        </div>
                        <TextArea label="Description" value={c.desc} onChange={(v) => setPath(`process.chapters.${i}.desc`, v)} />
                      </div>
                    ))}
                  </div>
                </Group>

                <Group title="Pricing">
                  <TextField label="Title" value={data.pricing.title} onChange={(v) => setPath('pricing.title', v)} />
                  <TextField label="Subtitle" value={data.pricing.subtitle} onChange={(v) => setPath('pricing.subtitle', v)} />
                  <div className="space-y-6">
                    {data.pricing.plans.map((p, i) => (
                      <div key={i} className="border border-[var(--line)] p-6 bg-paper-2">
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-ember mb-4">Plan {i + 1}</div>
                        <div className="grid grid-cols-2 gap-6 mb-4">
                          <TextField label="Name" value={p.name} onChange={(v) => setPath(`pricing.plans.${i}.name`, v)} />
                          <TextField label="Price" value={p.price} onChange={(v) => setPath(`pricing.plans.${i}.price`, v)} />
                        </div>
                        <div className="grid grid-cols-2 gap-6 mb-4">
                          <TextField label="Period" value={p.period} onChange={(v) => setPath(`pricing.plans.${i}.period`, v)} />
                          <TextField label="CTA" value={p.cta} onChange={(v) => setPath(`pricing.plans.${i}.cta`, v)} />
                        </div>
                        <TextArea label="Description" value={p.description} onChange={(v) => setPath(`pricing.plans.${i}.description`, v)} />
                        <TextArea
                          label="Features (one per line)"
                          value={(p.features || []).join('\n')}
                          onChange={(v) => setPath(`pricing.plans.${i}.features`, v.split('\n'))}
                        />
                        <label className="flex items-center gap-3 mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-dim">
                          <input
                            type="checkbox"
                            checked={!!p.featured}
                            onChange={(e) => setPath(`pricing.plans.${i}.featured`, e.target.checked)}
                          />
                          Featured plan
                        </label>
                      </div>
                    ))}
                  </div>
                </Group>

                <Group title="FAQ">
                  <TextField label="Title" value={data.faq.title} onChange={(v) => setPath('faq.title', v)} />
                  <div className="space-y-6">
                    {data.faq.items.map((it, i) => (
                      <div key={i} className="border border-[var(--line)] p-6 bg-paper-2">
                        <TextField label={`Question ${i + 1}`} value={it.q} onChange={(v) => setPath(`faq.items.${i}.q`, v)} />
                        <TextArea label="Answer" value={it.a} onChange={(v) => setPath(`faq.items.${i}.a`, v)} />
                      </div>
                    ))}
                  </div>
                </Group>

                <Group title="Contact">
                  <TextField label="Title" value={data.contact.title} onChange={(v) => setPath('contact.title', v)} />
                  <TextArea label="Subtitle" value={data.contact.subtitle} onChange={(v) => setPath('contact.subtitle', v)} />
                  <div className="grid grid-cols-2 gap-6">
                    <TextField label="Studio label" value={data.contact.office_label} onChange={(v) => setPath('contact.office_label', v)} />
                    <TextField label="Studio value" value={data.contact.office_value} onChange={(v) => setPath('contact.office_value', v)} />
                    <TextField label="Direct label" value={data.contact.email_contact_label} onChange={(v) => setPath('contact.email_contact_label', v)} />
                    <TextField label="Direct value" value={data.contact.email_contact_value} onChange={(v) => setPath('contact.email_contact_value', v)} />
                    <TextField label="Submit CTA" value={data.contact.submit} onChange={(v) => setPath('contact.submit', v)} />
                    <TextField label="Success toast" value={data.contact.success} onChange={(v) => setPath('contact.success', v)} />
                  </div>
                </Group>

                <Group title="Footer">
                  <TextArea label="Tagline" value={data.footer.tagline} onChange={(v) => setPath('footer.tagline', v)} />
                  <TextField label="Rights text" value={data.footer.rights} onChange={(v) => setPath('footer.rights', v)} />
                </Group>
              </div>
            )}
          </>
        )}

        {tab === 'messages' && (
          <>
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-3">
                  § Inbox · Contact briefs
                </div>
                <h1 className="font-serif text-4xl md:text-5xl leading-tight">
                  Messages
                  <span className="ml-3 font-mono text-lg text-ink-dim">
                    ({messages.length})
                  </span>
                </h1>
              </div>
              <button
                onClick={loadMessages}
                className="btn-ghost !px-3 !py-1.5 !text-[10px]"
                data-testid="admin-messages-refresh"
              >
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Refresh
              </button>
            </div>

            {loadingMsgs && (
              <div className="font-mono text-xs uppercase tracking-widest text-ink-dim">
                …loading
              </div>
            )}

            {!loadingMsgs && messages.length === 0 && (
              <div className="border border-[var(--line)] p-16 text-center text-ink-dim italic font-serif text-lg">
                No messages yet. The forge is quiet.
              </div>
            )}

            {!loadingMsgs && messages.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <ul className="lg:col-span-5 space-y-2">
                  {messages.map((m) => (
                    <li key={m.id}>
                      <button
                        onClick={() => { setSelectedMsg(m); if (!m.read) onMarkRead(m.id); }}
                        data-testid={`admin-msg-${m.id}`}
                        className={`w-full text-left p-5 border transition-colors ${
                          selectedMsg?.id === m.id
                            ? 'border-ember bg-[rgba(255,90,31,0.05)]'
                            : 'border-[var(--line)] bg-paper-2 hover:border-[var(--line-strong)]'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-serif text-lg">{m.name}</div>
                          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim">
                            {new Date(m.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-ink-dim text-sm truncate mb-1">{m.subject || '(no subject)'}</div>
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em]">
                          {m.read ? (
                            <><MailOpen className="w-3 h-3 text-ink-dim" /> <span className="text-ink-dim">Read</span></>
                          ) : (
                            <><Mail className="w-3 h-3 text-ember" /> <span className="text-ember">New</span></>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="lg:col-span-7">
                  {selectedMsg ? (
                    <article className="border border-[var(--line)] bg-paper-2 p-8 md:p-10 clip-frame">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-ember mb-2">
                            {new Date(selectedMsg.created_at).toLocaleString()}
                          </div>
                          <h2 className="font-serif text-3xl">{selectedMsg.name}</h2>
                          <a
                            href={`mailto:${selectedMsg.email}`}
                            className="text-ember text-sm link-ember"
                          >
                            {selectedMsg.email}
                          </a>
                        </div>
                        <button
                          onClick={() => onDeleteMsg(selectedMsg.id)}
                          className="btn-ghost !px-3 !py-1.5 !text-[10px] !border-destructive/50 hover:!border-destructive !text-destructive"
                          data-testid="admin-msg-delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                        </button>
                      </div>
                      {selectedMsg.subject && (
                        <div className="font-serif italic text-xl text-ink mb-6">
                          {selectedMsg.subject}
                        </div>
                      )}
                      <div className="whitespace-pre-wrap text-ink text-base leading-relaxed">
                        {selectedMsg.message}
                      </div>
                    </article>
                  ) : (
                    <div className="border border-[var(--line)] p-16 text-center text-ink-dim italic font-serif">
                      Select a message to read.
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <section>
      <h3 className="font-serif text-2xl md:text-3xl mb-6 pb-3 border-b border-[var(--line)]">
        {title}
      </h3>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-2">
        {label}
      </label>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-paper-3 border border-[var(--line)] focus:border-ember focus:outline-none px-3 py-2 text-ink text-sm"
      />
    </div>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-2">
        {label}
      </label>
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-paper-3 border border-[var(--line)] focus:border-ember focus:outline-none px-3 py-2 text-ink text-sm resize-y"
      />
    </div>
  );
}
